import type { BlockNoteEditor } from "@blocknote/core";
import {
  preferTokenizedDomUrl,
  resolveImageDataUrl,
} from "./briefExportImageResolve";
import {
  blockPlainText,
  collectBlocks,
  collectImageBlocks,
  syncTokenizedUrlsFromDom,
} from "./briefExportMedia";

export type PdfContentItem =
  | { kind: "heading" | "text"; text: string }
  | { kind: "image"; dataUrl: string; caption?: string };

export async function buildPdfContent(
  editor: BlockNoteEditor
): Promise<{ items: PdfContentItem[]; mediaTotal: number; mediaFailed: number }> {
  syncTokenizedUrlsFromDom(editor);

  const items: PdfContentItem[] = [];
  const imageBlocks = collectImageBlocks(editor);
  const handled = new Set<string>();
  let mediaFailed = 0;

  const addImage = async (url: string, caption?: string) => {
    const key = preferTokenizedDomUrl(url);
    if (handled.has(key)) return;
    handled.add(key);
    try {
      items.push({
        kind: "image",
        dataUrl: await resolveImageDataUrl(key),
        caption,
      });
    } catch (error) {
      mediaFailed += 1;
      console.warn("[briefExport] Failed to embed image:", url, error);
    }
  };

  for (const block of collectBlocks(editor.document)) {
    const blockUrl = (block.props as { url?: string } | undefined)?.url;

    if (block.type === "image" && blockUrl) {
      const props = block.props as { caption?: string; name?: string };
      await addImage(blockUrl, props.caption || props.name);
      continue;
    }

    const text = blockPlainText(block).trim();
    if (!text) continue;
    if (imageBlocks.some((img) => img.url === text)) continue;

    items.push({
      kind: block.type === "heading" ? "heading" : "text",
      text,
    });
  }

  return {
    items,
    mediaTotal: imageBlocks.length,
    mediaFailed,
  };
}
